<template>
  <div :class="[containerClass, tabbed ? 'tabbed-container' : '']" class="overflow-hidden">
    <div v-if="!tabbed" class="d-flex justify-center align-center font-size-huge" :class="[colorClass + '--background', 'text-center', 'white--text', 'barlow-condensed-bold']">
      {{ year }}
    </div>
    <div class="d-flex flex-column white justify-center align-center pa-6">
      <div 
        v-for="(set, index) in yearGroup.tourSets" 
        :key="set.id" 
        class="width-100 pa-2 my-4 d-flex flex-column align-center"
        :class="{ 'cursor-pointer': isTourOpen(set.tour), 'cursor-not-allowed': !isTourOpen(set.tour) }"
        @click="handleTourClick(set)"
      >
        <!-- Date-->
        <div class="d-flex flex-column flex-md-row justify-center align-center width-100" :class="{ 'pb-12': index < yearGroup.tourSets.length - 1 }">
          <div :class="['flex-grow-1', 'barlow-condensed-semibold', dateTextClass]" class="font-size-xl line-height-4">
            {{ formatDates(set.mainDates) }}
          </div>
          <v-btn class="mt-6 mt-md-0" :color="colorClass == 'renedian-sand-grey' ? '#5a6660' : '#bd4426'" elevation="0" width="130px" large :outlined="!isTourOpen(set.tour)" :disabled="!isTourOpen(set.tour)" :style="{ color: isTourOpen(set.tour) ? 'white' : (colorClass == 'renedian-sand-grey' ? 'white' : '#bd4426') }">
            <span class="barlow-regular font-weight-bold">{{ getDisplayStatusText(set.tour, 'primary') }}</span>
          </v-btn>
        </div>
        <!-- Divider between tour sets -->
        <div v-if="index < yearGroup.tourSets.length - 1" :class="[colorClass + '--background', 'width-60']" style="height: 2px;"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { tourState } from '@/schema/tourState'
import { phaseOf } from '@/schema/tourPhases'

export default {
  name: 'TourDetailsDates',
  props: {
    year: {
      type: Number,
      required: true
    },
    color: {
      type: String,
      required: true
    },
    yearGroup: {
      type: Object,
      required: true
    },
    template: {
      type: Object,
      default: null
    },
    isTourBookable: {
      type: Function,
      default: null
    },
    tabbed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    colorClass() {
      return this.color
    },
    dateTextClass() {
      return this.color === 'renedian-sand-grey'
        ? 'renedian-sand-green--text'
        : 'renedian-on-light--text'
    },
    containerClass() {
      if (this.color === 'renedian-burnt-orange') {
        return 'current-year-container'
      } else if (this.color === 'renedian-sand-grey') {
        return 'next-year-container'
      }
      return ''
    },
    currentYear() {
      return new Date().getFullYear()
    }
  },
  methods: {
    formatDates(dateString) {
      // Convert "MAR 9 - MAR 13" format or similar
      // The dateString should already be formatted from parent, but we'll ensure it's uppercase
      return dateString ? dateString.toUpperCase() : ''
    },
    getDisplayStatus(tour, scheduleType = 'primary') {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      
      // Get the start and finish dates for the schedule type
      const startDate = scheduleType === 'excursion' 
        ? new Date(tour.schedule?.excursion?.start)
        : new Date(tour.schedule?.primary?.start)
      
      const finishDate = scheduleType === 'excursion'
        ? new Date(tour.schedule?.excursion?.finish)
        : new Date(tour.schedule?.primary?.finish)
      
      if (!startDate || isNaN(startDate.getTime())) {
        return 'CLOSED'
      }
      
      startDate.setHours(0, 0, 0, 0)
      if (finishDate && !isNaN(finishDate.getTime())) {
        finishDate.setHours(0, 0, 0, 0)
      }
      const tourYear = startDate.getFullYear()
      
      // First check: Is the tour completed?
      // Tour is completed if finish date has passed, or if start date has passed and no finish date
      const isCompleted = finishDate && !isNaN(finishDate.getTime())
        ? finishDate < now
        : startDate < now
      
      if (isCompleted) {
        // When completed and not in the current calendar year
        if (tourYear < this.currentYear) {
          return `COMPLETE - ${tourYear}`
        } else {
          // When completed in current year
          return 'COMPLETE'
        }
      }
      
      // Second check: Is the tour in progress?
      // Tour is in progress if current date falls within the date range (inclusively)
      if (finishDate && !isNaN(finishDate.getTime())) {
        if (startDate <= now && finishDate >= now) {
          return 'IN PROGRESS'
        }
      }
      
      // Third check: Potentially open tours (not completed, not in progress), by the
      // tour's state (schema/tourState), whichever shape the document has.
      const state = tourState(tour)
      if (state.cancelled || state.status === 'closed' || phaseOf(tour) === 'archived') {
        return 'CLOSED'
      }
      
      if (state.visibility === 'hidden') {
        return 'PRIVATE'
      }
      
      if (state.status === 'full') {
        return 'FULL'
      }
      
      // Open - calculate capacity percentage
      if (state.status === 'open') {
        const total = tour.availability?.spots ?? 12
        
        if (total === 0) {
          return 'CLOSED'
        }
        
        // Calculate remaining spots from bookings if available, otherwise use availability.remaining
        let remaining = 0
        if (tour.bookings && Array.isArray(tour.bookings)) {
          // Calculate total guests from bookings (sum of guests.length for each booking)
          const totalGuests = tour.bookings.reduce((sum, booking) => {
            return sum + (booking.guests?.length || 0)
          }, 0)
          remaining = Math.max(0, total - totalGuests)
        } else {
          // Fallback to availability.remaining if bookings not available
          remaining = tour.availability?.remaining ?? 0
        }
        
        // Calculate capacity percentage (booked spots / total spots)
        const booked = total - remaining
        const capacityPercent = (booked / total) * 100
        
        // 100%+ capacity = Limited Availability
        if (capacityPercent >= 100) {
          return 'LIMITED AVAILABILITY'
        }
        
        // 50-99% capacity = Almost Full
        if (capacityPercent >= 50 && capacityPercent < 100) {
          return 'ALMOST FULL'
        }
        
        // 0-50% capacity = Open
        return 'OPEN'
      }
      
      return 'CLOSED'
    },
    getDisplayStatusText(tour, scheduleType = 'primary') {
      const status = this.getDisplayStatus(tour, scheduleType)
      
      if (status === 'FULL') {
        return 'FULL'
      }
      
      if (status === 'PRIVATE') {
        return 'PRIVATE TOUR'
      }
      
      if (status === 'IN PROGRESS') {
        return 'IN PROGRESS'
      }
      
      // Handle multi-line status for "ALMOST FULL"
      if (status === 'ALMOST FULL') {
        return 'ALMOST\nFULL'
      }
      
      return status
    },
    getStatusClass(status) {
      // Extract base status for class (remove year suffix if present)
      const baseStatus = status.includes('COMPLETE') ? 'COMPLETE' : status.split('\n')[0]
      
      return {
        'status-closed': baseStatus === 'CLOSED' || baseStatus.includes('COMPLETE'),
        'status-open': baseStatus === 'OPEN',
        'status-almost-full': baseStatus === 'ALMOST FULL',
        'status-limited': baseStatus === 'LIMITED AVAILABILITY',
        'status-full': baseStatus === 'FULL',
        'status-private': baseStatus === 'PRIVATE',
        'status-in-progress': baseStatus === 'IN PROGRESS'
      }
    },
    isTourOpen(tour) {
      const status = this.getDisplayStatus(tour, 'primary')
      // If status is PRIVATE, check if template is marked as private
      if (status === 'PRIVATE') {
        return this.template?.type === 'private'
      }
      
      // Only these states are considered "open" and bookable
      return status === 'OPEN' || status === 'ALMOST FULL' || status === 'LIMITED AVAILABILITY'
    },
    handleTourClick(set) {
      // Only navigate if tour is in an open state
      if (this.isTourOpen(set.tour)) {
        this.go('BookingWithId', { id: set.id })
      }
    }
  }
}
</script>

<style scoped>
.current-year-container {
  border: 2px solid #b35438;
  border-radius: 30px;
}
.next-year-container {
  border: 2px solid #5a6660;
  border-radius: 30px;
}

.status-button {
  border: 1px solid #8b7355;
  border-radius: 4px;
  padding: 8px 16px;
  text-align: center;
  white-space: pre-line;
  min-width: 120px;
  font-family: 'Barlow', sans-serif;
  font-weight: 500;
  font-size: 0.9rem;
  line-height: 1.3;
}

.status-closed {
  color: #8b7355;
  background-color: transparent;
}

.status-open {
  color: #8b7355;
  background-color: transparent;
}

.status-almost-full {
  color: #8b7355;
  background-color: transparent;
}

.status-limited {
  color: #8b7355;
  background-color: transparent;
}

.status-full {
  color: #8b7355;
  background-color: transparent;
}

.status-private {
  color: #8b7355;
  background-color: transparent;
}

.status-in-progress {
  color: #8b7355;
  background-color: transparent;
}

.status-available {
  color: #2a261b;
  background-color: transparent;
  border-color: #2a261b;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.cursor-not-allowed .v-btn {
  opacity: 0.6;
}

.date-text {
  color: #b35438; /* Reddish-brown color for dates */
}

.tabbed-container {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}
</style>
